import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layers, Sparkles, Search, LayoutGrid, Smartphone, ShoppingCart, Code, MousePointer, ChevronDown, CheckCircle, ArrowRight, Download, Zap, ListTree } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const HERO_IMG = `${BACKEND_URL}/api/builder/media/files/products/ai-joomla-mobile-menu/images/ai_joomla_mobile_menu_hero.png`;
const BOX_IMG  = `${BACKEND_URL}/api/builder/media/files/products/ai-joomla-mobile-menu/images/ai_joomla_mobile_menu_box.png`;

// ── Feature cards ──────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: Layers,       title: '10+ Menu Types',        desc: 'Slide, Drop, Tree, Accordion, List, Grid and more — pick a sidebar or overlay layout that fits your theme.' },
  { icon: Sparkles,     title: '50+ Animations',        desc: 'Dozens of sidebar slide-in and overlay reveal animations, all switchable from a dropdown — no CSS required.' },
  { icon: ListTree,     title: 'Font Awesome Icons',     desc: 'Assign an icon to every menu item straight from the built-in Font Awesome picker, or upload your own image.' },
  { icon: Search,       title: 'Live Filter Search',     desc: 'A built-in instant search box lets visitors jump straight to the page they need as they type.' },
  { icon: LayoutGrid,   title: 'Module Positions',       desc: 'Drop any Joomla module — a search bar, social icons, a mini-cart — into the menu header, footer, or per submenu.' },
  { icon: MousePointer, title: 'Smooth-Scroll Nav',       desc: 'One-page smooth-scroll navigation with automatic active-section highlighting as visitors scroll.' },
  { icon: Smartphone,   title: 'Touch & Swipe Optimized', desc: 'Fully touch-friendly with swipe-to-close gestures and tap targets sized correctly for mobile fingers.' },
  { icon: ShoppingCart, title: 'E-Commerce Ready',       desc: 'Native category & product menus for VirtueMart, HikaShop, JoomShopping, Redshop, MijoShop, plus K2 and EasyBlog.' },
  { icon: Code,         title: 'Developer API',          desc: 'A JavaScript API and template overrides give developers full control for deep, custom integrations.' },
];

// ── What's included ─────────────────────────────────────────────────────────────
const INCLUDED = [
  '10+ menu types (Slide, Drop, Tree, Accordion, List, Grid & more)',
  '50+ sidebar & overlay animations',
  'Font Awesome icon picker on every menu item',
  'Built-in live filter / instant search',
  'Module positions inside the menu at every level',
  'Smooth-scroll one-page navigation',
  'Native VirtueMart, HikaShop, JoomShopping, Redshop, MijoShop, K2 & EasyBlog menus',
  'JavaScript API & template overrides for developers',
  'Automatic updates via the AI Joomla license key',
];

// ── FAQ ────────────────────────────────────────────────────────────────────────
const FAQS = [
  { q: 'Does it work with Joomla 5 and 6?', a: 'Yes — AI Joomla Mobile Menu is tested and supported on Joomla 5.x and 6.x, with PHP 8.1 through 8.5.' },
  { q: 'Can I use it alongside my existing main menu?', a: 'Yes. It installs as a module you publish to any position — your existing Joomla menu, template, and other modules stay completely untouched.' },
  { q: 'Does it support my e-commerce store\u2019s categories?', a: 'Yes — it can build its menu directly from VirtueMart, HikaShop, JoomShopping, Redshop or MijoShop categories, plus K2 categories and EasyBlog.' },
  { q: 'Can every menu item have its own icon?', a: 'Yes — pick one from the built-in Font Awesome icon library per menu item, or upload a custom image instead.' },
  { q: 'Will it slow down my site?', a: 'No. Its assets only load on pages where the module is actually published, and the shared loader is cached across page views.' },
  { q: 'What happens if my license lapses?', a: 'Your menu keeps working exactly as installed. You\u2019ll just stop receiving new version updates and support until the license is renewed.' },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button onClick={() => setOpen(!open)} className="w-full text-left py-5 flex justify-between items-center gap-4 group" data-testid={`faq-toggle-${q.slice(0, 12).replace(/\s+/g, '-').toLowerCase()}`}>
        <span className="text-white font-medium group-hover:text-teal-400 transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="pb-5 text-gray-400 leading-relaxed text-sm">{a}</p>}
    </div>
  );
}

export default function AiJoomlaMobileMenuPage() {
  return (
    <div className="min-h-screen bg-bots-dark text-white font-sans">
      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_60%_0%,_rgba(20,184,166,0.15),_transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 text-xs font-semibold uppercase tracking-wide mb-6">
              <Layers className="w-3.5 h-3.5" /> Joomla Navigation Module
            </div>
            <h1 className="text-5xl sm:text-6xl font-extrabold leading-tight mb-6">
              AI Joomla<br />
              <span className="bg-gradient-to-r from-teal-400 to-cyan-500 bg-clip-text text-transparent">Mobile Menu</span>
            </h1>
            <p className="text-gray-300 text-lg leading-relaxed mb-8 max-w-xl">
              A fully responsive, touch-optimized vertical menu for Joomla 5 &amp; 6. 10+ menu types,
              50+ animations, Font Awesome icons, and native e-commerce category support — no coding required.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/extensions/ai-joomla-mobile-menu"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-teal-500 hover:bg-teal-400 text-white font-bold transition-all hover:shadow-lg hover:shadow-teal-500/30"
                data-testid="cta-buy-now"
              >
                <Zap className="w-4 h-4" /> Buy Now — $39
              </Link>
              <Link
                to="/extensions"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 hover:border-white/40 text-white font-medium transition-all"
                data-testid="cta-browse-extensions"
              >
                <ArrowRight className="w-4 h-4" /> Browse All Extensions
              </Link>
            </div>
            <div className="flex flex-wrap gap-4 mt-8 text-xs text-gray-400">
              {['Joomla 5 & 6','PHP 8.1 – 8.5','Touch & Swipe Ready','No coding required'].map(b => (
                <span key={b} className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" />{b}</span>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={HERO_IMG} alt="AI Joomla Mobile Menu" className="w-full rounded-2xl shadow-2xl shadow-black/50 border border-white/10" />
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-teal-500/10 rounded-full blur-2xl" />
          </div>
        </div>
      </section>

      {/* ── STATS BAR ─────────────────────────────────────────────────────── */}
      <section className="bg-white/5 border-y border-white/10 py-6">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[['10+','Menu Types'],['50+','Animations'],['5','Store Integrations'],['1-Click','Install & Update']].map(([n, l]) => (
            <div key={l}>
              <p className="text-3xl font-extrabold text-teal-400">{n}</p>
              <p className="text-gray-400 text-sm mt-1">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES GRID ─────────────────────────────────────────────────── */}
      <section className="py-24 max-w-7xl mx-auto px-4">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Everything you need for a modern menu</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">From animated sidebars to native e-commerce categories — all configured from inside the Joomla module manager, no template edits required.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="group bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-teal-400/40 hover:bg-teal-400/5 transition-all">
              <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center mb-4 group-hover:bg-teal-500/20 transition-all">
                <Icon className="w-5 h-5 text-teal-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-2">{title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHAT'S INCLUDED + PRICING ───────────────────────────────────────── */}
      <section className="bg-white/3 border-y border-white/10 py-24">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">What's included</h2>
            <ul className="space-y-3">
              {INCLUDED.map(item => (
                <li key={item} className="flex items-start gap-3 text-gray-300 text-sm" data-testid={`included-${item.slice(0, 10).replace(/\s+/g, '-').toLowerCase()}`}>
                  <CheckCircle className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-teal-400/30 p-8 text-center bg-teal-400/5">
            <img src={BOX_IMG} alt="AI Joomla Mobile Menu" className="w-40 mx-auto mb-6 rounded-xl" />
            <h3 className="text-2xl font-bold text-white mb-1">Single Site License</h3>
            <p className="text-4xl font-extrabold text-teal-400 mb-2">$39</p>
            <p className="text-gray-400 text-sm mb-6">One-time purchase, includes updates &amp; support.</p>
            <Link
              to="/extensions/ai-joomla-mobile-menu"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-teal-500 hover:bg-teal-400 text-white text-sm font-bold transition-all w-full justify-center"
              data-testid="pricing-buy-now-btn"
            >
              <Download className="w-4 h-4" /> Buy Now
            </Link>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white text-center mb-10">Frequently asked questions</h2>
          {FAQS.map(faq => <FaqItem key={faq.q} {...faq} />)}
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white/3 border-t border-white/10">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Layers className="w-12 h-12 text-teal-400 mx-auto mb-6" />
          <h2 className="text-4xl font-extrabold text-white mb-4">Ready for a better mobile menu?</h2>
          <p className="text-gray-400 text-lg mb-8">Install in minutes on any Joomla 5 or 6 site — no coding, no template edits.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/extensions/ai-joomla-mobile-menu"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-teal-500 hover:bg-teal-400 text-white font-bold text-lg transition-all hover:shadow-xl hover:shadow-teal-500/30"
              data-testid="final-cta-buy-now">
              <Zap className="w-5 h-5" /> Buy Now — $39
            </Link>
            <Link to="/docs/ai-joomla-mobile-menu"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 hover:border-white/40 text-white font-medium text-lg transition-all"
              data-testid="final-cta-view-docs">
              View Documentation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
